import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar";
import { useCart, getCartItemCount } from "../../hooks/useCart";
import BottomNav from "../../components/BottomNav";
import Button from "Shared/Button";
import AddressList from "components/AddressList";
import AddressSettingsList from "components/AddressSettingsList";
import AddressModal from "components/AddressModal";
import ValidateAddressModal from "components/ValidateAddressModal";
import { useApiErrorMessage } from "hooks/useApiErrorMessage";
import styles from "./CartAddressPage.module.css";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useAddresses,
  useCreateAddress,
  useValidateAddress,
} from "../../hooks/useAddresses";

export function CartAddressPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("Cart");
  const [isManagingAddresses, setIsManagingAddresses] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [validationError, setValidationError] = useState("");
  const apiErrorMessage = useApiErrorMessage();
  const [suggestedAddress, setSuggestedAddress] = useState(null);
  const [currentAddressForEdit] = useState(null);

  const history = useHistory();

  const { data: apiAddresses = [] } = useAddresses();
  const createAddress = useCreateAddress();
  const validateAddress = useValidateAddress();
  const [addressData, setAddressData] = useState([]);

  useEffect(() => {
    setAddressData(apiAddresses);
  }, [apiAddresses]);

  const handleAddAddressClick = () => {
    setCurrentAddress(null);
    setValidationError("");
    setShowAddressModal(true);
  };

  const handleAddressModalClose = () => {
    setValidationError("");
    setShowAddressModal(false);
  };

  const handleAddressValidate = (addressData) => {
    setCurrentAddress(addressData);
    setValidationError("");
    validateAddress.mutate(addressData, {
      onSuccess: (suggested) => {
        setSuggestedAddress(suggested ?? addressData);
        setShowAddressModal(false);
        setShowValidateModal(true);
      },
      onError: (err) => {
        setValidationError(
          apiErrorMessage(
            err,
            t(
              "errorModal.validateAddress",
              "We could not validate your address. Please check it and try again."
            )
          )
        );
      },
    });
  };

  const handleUseSuggested = () => {
    setShowValidateModal(false);
    if (suggestedAddress) {
      createAddress.mutate(suggestedAddress);
    }
  };

  const handleUseOriginal = () => {
    setShowValidateModal(false);
    if (currentAddress) {
      createAddress.mutate(currentAddress);
    }
  };

  const handleEditOriginal = () => {
    setShowValidateModal(false);
    setShowAddressModal(true);
  };

  const handleValidateModalClose = () => {
    setShowValidateModal(false);
  };

  const handleTabClick = (item) => {
    setActiveTab(item.label);
    history.push(item.href);
  };

  const menuItems = [
    { label: "WWG Store", href: "/wwg-store" },
    { label: "My Lists", href: "/my-lists" },
    { label: "Digital Delivery", href: "/digital-delivery" },
    { label: "Cart", href: "/cart" },
  ];

  const hasAddresses = addressData.length > 0;

  const handleManageAddressesClick = () => {
    setIsManagingAddresses(!isManagingAddresses);
  };

  const handleAddressUpdate = (updatedAddresses) => {
    setAddressData(updatedAddresses);
  };

  const { data: cartData } = useCart();
  const cartCount = getCartItemCount(cartData);

  return (
    <div className={styles.container}>
      <div className={styles.desktopNav}>
        <Navbar
          onClick={() => history.push(`/cart`)}
          isHome={false}
          menuItems={menuItems}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          cartCount={cartCount}
        />
      </div>

      {/* Page content */}
      <div className={styles.pageContent}>
        <div className={styles.buttonContainer}>
          <Button
            text={t("Add Address")}
            className="closeBtn"
            onClick={handleAddAddressClick}
          />
          {hasAddresses && (
            <Button
              text={t("Manage My Addresses")}
              className="editBtn"
              onClick={handleManageAddressesClick}
            />
          )}
        </div>

        {isManagingAddresses ? (
          <AddressSettingsList
            addressList={addressData}
            onAddressUpdate={handleAddressUpdate}
          />
        ) : (
          hasAddresses && <AddressList addressInfo={addressData} />
        )}
      </div>

      <AddressModal
        address={currentAddress || currentAddressForEdit}
        isOpen={showAddressModal}
        onClose={handleAddressModalClose}
        onValidate={handleAddressValidate}
        errorMessage={validationError}
        title={t("Add Address")}
      />

      <ValidateAddressModal
        addressInfo={currentAddress}
        suggestedAddress={suggestedAddress}
        isOpen={showValidateModal}
        onClose={handleValidateModalClose}
        onUseSuggested={() => handleUseSuggested(currentAddress)}
        onUseOriginal={() => handleUseOriginal(currentAddress)}
        onEditOriginal={handleEditOriginal}
      />

      {/* Mobile bottom nav only */}
      <BottomNav cartCount={cartCount} />
    </div>
  );
}
